import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash, InstagramLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function InstagramSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ url: '', caption: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getInstagramPostsAdmin(); setPosts(res.data); }
    catch { toast.error('Errore caricamento post'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    try {
      await api.addInstagramPost({ url: form.url.trim(), caption: form.caption.trim() });
      toast.success('Post aggiunto');
      setShowModal(false);
      setForm({ url: '', caption: '' });
      load();
    } catch { toast.error('Errore aggiunta post'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo post?')) return;
    try { await api.deleteInstagramPost(id); toast.success('Post eliminato'); load(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const extractPostInfo = (url) => {
    const match = url.match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel)\/([A-Za-z0-9_-]+)/);
    const isReel = url.includes('/reel/');
    return match ? { id: match[1], type: isReel ? 'reel' : 'p' } : null;
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 animate-fade-in">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Instagram Feed</h1>
          <p className="text-[#52525B] text-sm">Aggiungi i link dei post Instagram per mostrarli nella homepage</p>
        </div>
        <Button onClick={() => { setForm({ url: '', caption: '' }); setShowModal(true); }} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] rounded-sm" data-testid="add-instagram-btn">
          <Plus size={16} className="mr-1" /> Aggiungi Post
        </Button>
      </div>

      <div className="space-y-4 animate-fade-in animate-delay-1">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : posts.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
            <InstagramLogo size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessun post Instagram. Aggiungi il primo!</p>
            <p className="text-[#A1A1AA] text-xs mt-1">Copia il link di un post o reel da Instagram e incollalo qui</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map(p => {
              const info = extractPostInfo(p.url);
              return (
                <div key={p.id} className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid={`instagram-post-${p.id}`}>
                  {info ? (
                    <iframe
                      src={`https://www.instagram.com/${info.type}/${info.id}/embed`}
                      className="w-full h-[350px] border-0"
                      loading="lazy"
                      title={`Instagram ${info.id}`}
                    />
                  ) : (
                    <div className="h-[200px] flex items-center justify-center bg-[#F4F4F5] text-[#A1A1AA] text-sm p-4 text-center">
                      <div><InstagramLogo size={32} className="mx-auto mb-2" /><p className="break-all text-xs">{p.url}</p></div>
                    </div>
                  )}
                  <div className="p-3 border-t border-[#E4E4E7]">
                    {p.caption && <p className="text-sm text-[#52525B] mb-2">{p.caption}</p>}
                    <div className="flex items-center justify-between">
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate flex-1 mr-2">Apri su Instagram</a>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-instagram-${p.id}`}><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Aggiungi Post Instagram</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Link del Post</Label>
              <Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://www.instagram.com/p/..." required className="rounded-sm" data-testid="instagram-url-input" />
              <p className="text-[10px] text-[#A1A1AA]">Copia il link da Instagram (post o reel)</p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Didascalia (opzionale)</Label>
              <Input value={form.caption} onChange={e => setForm({...form, caption: e.target.value})} placeholder="Es. Saggio di fine anno..." className="rounded-sm" data-testid="instagram-caption-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="instagram-save-btn">Aggiungi</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
